import Select from 'react-select'
import React, { useMemo, useState } from 'react'
import { Checkbox, Drawer } from 'antd'
import Creatable from 'react-select/creatable'

import { useToggle } from '../../modules'
import {
    getAvailableDataSources,
    getFormDataSource,
    getPreviousValuePayload,
    inferFieldDataSourceKey,
} from '../../formDataSources'

import Confirm from '../reusable/confirm'

const FormField = ({ toggle, visible, field, index, onSave }) => {
    const [name, setName] = useState(field?.name)
    const [type, setType] = useState(field?.type)
    const [source, setSource] = useState(field?.source)
    const [payload, setPayload] = useState(field?.payload)
    const [dataSourceKey, setDataSourceKey] = useState(inferFieldDataSourceKey(field))
    const [unit, setUnit] = useState(field?.unit)
    const [group, setGroup] = useState(field?.group)
    const [options, setOptions] = useState(field?.options ?? [])

    const [creatable, toggleCreatable] = useToggle(field?.creatable)

    const close = () => {
        toggle()
    }

    const types = [
        'Text',
        'Textarea',
        'IncrementalTextarea',
        'Number',
        'MultiFileUpload',
        'MultiImageUpload',
        'Date',
        'Date Range',
        'Select',
        'Multi Select'
    ]
    const sources = ['List', 'Query', 'Previous']

    const previousPayload = getPreviousValuePayload({ ...field, name, payload })
    const selectedDataSource = getFormDataSource(dataSourceKey)
    const dataSourceOptions = useMemo(() => {
        const options = getAvailableDataSources(type?.value)
        if (source?.value === 'Previous')
            return options.filter(option => option.value === 'previous_values')

        return options.filter(option => option.value !== 'previous_values')
    }, [source, type])

    const save = () => Confirm({
        title: 'Save changes?',
        message: 'Are you sure you want to save these changes?',
        okText: 'Save',
        onOk: () => {
            onSave({
                field: {
                    name,
                    type,
                    unit,
                    group,
                    source,
                    dataSourceKey,
                    creatable,
                    options,
                    payload
                },
                i: index
            })
            close()
        }
    })

    const clearVals = () => {
        setOptions([])
        setDataSourceKey()
        setPayload()
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    <button onClick={ save } className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save Changes</button>
                </div>
            }
            width="30%">
            <p className="font-extrabold text-3xl mb-0">Form Fields</p>
            <div>
                <div className="flex flex-wrap mt-3 -mx-3">
                    <div className="w-full px-3 md:mb-0">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                            Field Name
                        </label>
                        <input
                            onChange={ e => setName(e.target.value) }
                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                            id="grid-role-name"
                            type="text"
                            value={ name }
                            name="name"
                            placeholder="Daily Visits" />
                    </div>
                </div>
                <div className="flex flex-wrap mt-3 -mx-3">
                    <div className="w-full px-3 md:mb-0">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                            Type
                        </label>
                        <Select
                            isClearable
                            value={ type }
                            onChange={ val => {
                                setType(val)
                                clearVals()
                            } }
                            styles={{ input: styles => ({ ...styles, minWidth: '10em' }) }}
                            options={ types?.map(typ => ({ label: typ, value: typ })) }
                            placeholder="Select Type" />
                    </div>
                </div>
                {
                    (type?.value === 'Select' || type?.value === 'Multi Select') &&
                    source?.value === 'List' &&
                    <div className="flex flex-wrap mt-3 -mx-3">
                        <div className="w-full px-3 md:mb-0">
                            <Checkbox checked={ creatable } onChange={ toggleCreatable }>Creatable</Checkbox>
                        </div>
                    </div>
                }
                <div className="flex flex-wrap mt-3 -mx-3">
                    <div className="w-full px-3 md:mb-0">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                            Unit
                        </label>
                        <input
                            onChange={ e => setUnit(e.target.value) }
                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                            id="grid-unit"
                            type="text"
                            value={ unit }
                            name="unit"
                            placeholder="mm/Hg" />
                    </div>
                </div>
                <div className="flex flex-wrap mt-3 -mx-3">
                    <div className="w-full px-3 md:mb-0">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                            Group
                        </label>
                        <input
                            onChange={ e => setGroup(e.target.value) }
                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-1 leading-tight focus:outline-none focus:bg-white"
                            id="grid-group"
                            type="text"
                            value={ group }
                            name="group"
                            placeholder="Visual Acuity" />
                        <p className="text-gray-500 mt-0 mb-3 text-xs italic">Fields sharing the same Group render side by side (2-3 works best).</p>
                    </div>
                </div>
                {
                    (type?.value === 'Select' || type?.value === 'Multi Select') &&
                    <div className="flex flex-wrap mt-3 -mx-3">
                        <div className="w-full px-3 md:mb-0">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Options Source
                            </label>
                            <Select
                                isClearable
                                value={ source }
                                onChange={ val => {
                                    setSource(val)
                                    clearVals()
                                    if (val?.value === 'Previous') {
                                        setDataSourceKey('previous_values')
                                        setPayload({ field: name ?? '', categories: [] })
                                    }
                                } }
                                styles={{ input: styles => ({ ...styles, minWidth: '10em' }) }}
                                options={ sources?.map(src => ({ label: src, value: src })) }
                                placeholder="Select Source" />
                        </div>
                    </div>
                }
                {
                    source ?
                        source?.value === 'List' ?
                            <div className="flex flex-wrap mt-2 -mx-3">
                                <div className="w-full px-3 mb-0">
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                        Options
                                    </label>
                                    <Creatable
                                        isMulti
                                        value={ options }
                                        options={ options }
                                        onChange={ setOptions }
                                        styles={{ input: styles => ({ ...styles, minWidth: '10em' }) }}
                                        onCreateOption={ val => setOptions(prev => [...prev, { label: val, value: val }]) }
                                        placeholder="Add Options" />
                                </div>
                            </div>
                        :
                            <>
                                <div className="flex flex-wrap mt-3 -mx-3">
                                    <div className="w-full px-3 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                            Data Source
                                        </label>
                                        <Select
                                            isClearable
                                            value={ selectedDataSource ? { label: selectedDataSource.label, value: selectedDataSource.key } : null }
                                            onChange={ val => {
                                                setDataSourceKey(val?.value)
                                                if (val?.value === 'previous_values')
                                                    setPayload({ field: previousPayload.field || name || '', categories: previousPayload.categories })
                                                else if (payload)
                                                    setPayload()
                                            } }
                                            options={ dataSourceOptions }
                                            placeholder="Select data source" />
                                    </div>
                                </div>
                                {
                                    dataSourceKey === 'previous_values' &&
                                    <>
                                        <div className="flex flex-wrap mt-3 -mx-3">
                                            <div className="w-full px-3 md:mb-0">
                                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                                    Previous Field Name
                                                </label>
                                                <input
                                                    onChange={ e => setPayload({ ...previousPayload, field: e.target.value }) }
                                                    className="appearance-none block my-0 w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                    id="grid-payload-field"
                                                    type="text"
                                                    value={ previousPayload.field }
                                                    name="payloadField"
                                                    placeholder="Diagnoses" />
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap mt-3 -mx-3">
                                            <div className="w-full px-3 md:mb-0">
                                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                                    Categories
                                                </label>
                                                <Creatable
                                                    isMulti
                                                    value={ previousPayload.categories.map(category => ({ label: category, value: category })) }
                                                    options={ previousPayload.categories.map(category => ({ label: category, value: category })) }
                                                    onChange={ vals => setPayload({ ...previousPayload, categories: (vals ?? []).map(val => val.value) }) }
                                                    onCreateOption={ val => setPayload({ ...previousPayload, categories: [...previousPayload.categories, val] }) }
                                                    placeholder="Add categories to search previous values from" />
                                            </div>
                                        </div>
                                    </>
                                }
                                {
                                    dataSourceKey === 'icd11_diagnoses' &&
                                    <p className="text-gray-500 my-0 text-xs italic">This field will use the built-in ICD-11 diagnosis picker instead of a custom endpoint.</p>
                                }
                            </>
                    : null
                }
            </div>
        </Drawer>
    )
}

export default FormField
